import { motion, useReducedMotion } from "motion/react";
import { useContext, type ComponentProps, type ReactNode, type Ref } from "react";
import { WeltKontext } from "../welt/kontext";
import type { IconTyp } from "./Briefgeist";
import { FEDER } from "./federn";

type Art = "primaer" | "sekundaer" | "ki" | "text" | "destruktiv" | "rot" | "passt";

/* Zustaende nach Handoff Schiefer (Uebergabe Abschnitt 4): Gedrueckt eine Stufe dunkler bzw. heller
   plus Skalierung 0,98, Deaktiviert 40 % Deckkraft ohne Zeiger, Fokus 2 px Akzent mit 2 px Abstand
   (global :focus-visible). */
const AUS = "disabled:cursor-not-allowed disabled:opacity-40";
const ART: Record<Art, string> = {
  primaer: `t-knopf rounded-btn px-4 bg-pg-accent text-pg-on-accent hover:bg-pg-accent-hover active:bg-pg-accent-hover ${AUS}`,
  // sekundaer: ohne Flaeche, 1 px Linie im Feldrand (keine Schatten, Regel 1). W22d-w5 (Verifier W22d-w4 H2):
  // field-border statt border-strong, denn beim Ueberfahren liegt innen surface-2 und border-strong hielt dort
  // hell nur 2,89:1; field-border haelt gegen Karte und surface-2 3,62 und 3,05:1, dunkel 4,10 und 3,36:1
  sekundaer: `t-knopf rounded-btn px-4 border border-pg-field-border text-pg-text hover:bg-pg-surface-2 active:bg-pg-surface-3 ${AUS}`,
  // KI: Postgelb, Text Petrol-dunkel (nur fuer KI-Handlungen wie „Brief erklaeren“)
  ki: `t-knopf rounded-btn px-4 bg-pg-ai-fill text-pg-on-ai active:brightness-92 ${AUS}`,
  text: `t-body px-2 text-pg-accent-text hover:opacity-70 ${AUS}`,
  destruktiv: `t-body px-2 text-pg-danger hover:opacity-70 ${AUS}`,
  // rot getoent: endgueltige Aktion im Sheet (Verwerfen, Loeschen)
  rot: `t-knopf rounded-btn px-4 bg-pg-danger-soft text-pg-danger ${AUS}`,
  // W82: Bestaetigen („Passt“); ausserhalb der W82-Welt wie der Primaerknopf
  passt: `t-knopf rounded-btn px-4 bg-pg-accent text-pg-on-accent hover:bg-pg-accent-hover active:bg-pg-accent-hover ${AUS}`,
};

type KnopfProps = Omit<ComponentProps<typeof motion.button>, "children"> & {
  art?: Art;
  gross?: boolean;
  breit?: boolean;
  icon?: IconTyp;
  children: ReactNode;
};

/** Knopf (Handoff Schiefer): Radius 10, Hoehe 48 (Web), Text 15/600 in der Titelschrift, ohne Versalien. */
export function Knopf({ art = "primaer", gross, breit, icon: Icon, children, className = "", ...rest }: KnopfProps) {
  const ruhig = useReducedMotion();
  // W82: unter data-welt drueckt der Knopf per CSS nach Fassung (welt.css), nicht mit der Schiefer-Feder
  const welt = useContext(WeltKontext);
  // Mindesthoehe statt fester Hoehe: bei grosser Schrift waechst der Knopf mit, der Text bricht
  // darin um statt herauszulaufen (Verifier W4 Runde 3, B4)
  const hoehe = art === "text" || art === "destruktiv" ? "min-h-(--touch)" : gross ? "min-h-(--btn-h-gross) py-2" : "min-h-(--btn-h) py-2";
  return (
    <motion.button
      type="button"
      whileTap={ruhig || welt || rest.disabled ? undefined : { scale: 0.98, transition: FEDER.druecken }}
      transition={FEDER.loslassen}
      data-art={art}
      className={`knopf inline-flex max-w-full items-center justify-center gap-2 text-center transition-colors duration-(--pg-dur-fast) ${hoehe} ${breit ? "w-full" : ""} ${ART[art]} ${className}`}
      {...rest}
    >
      {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0" />}
      {children}
    </motion.button>
  );
}

/** Icon-Button 44 x 44 (Navigationsleiste), aria-label Pflicht. ausgeklappt: schaltet etwas auf und zu
 *  (aria-expanded, getont solange offen). */
export function IconKnopf({ ref, ...props }: {
  icon: IconTyp;
  label: string;
  onClick?: () => void;
  getont?: boolean;
  ausgeklappt?: boolean;
  kennung?: string;
  /** Fokusziel, etwa nach dem Schliessen dessen, was der Knopf aufgeklappt hat (W22d-w8) */
  ref?: Ref<HTMLButtonElement>;
}) {
  const Icon = props.icon;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={props.label}
      aria-expanded={props.ausgeklappt}
      data-testid={props.kennung}
      onClick={props.onClick}
      className={`grid size-(--touch) place-items-center rounded-btn text-pg-accent-text hover:bg-pg-surface-2 ${props.getont || props.ausgeklappt ? "bg-pg-surface-2" : ""}`}
    >
      <Icon size={22} strokeWidth={1.5} aria-hidden />
    </button>
  );
}
